import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/text-button.js";
import "@material/web/icon/icon.js";

import "../components/app-shell";
import { sharedStyles } from "../styles/shared";
import { icon } from "../lib/icons";
import { errorMessage } from "../lib/dom";
import { toast } from "../lib/toast";
import { navigate } from "../lib/navigation";
import { AuthController } from "../lib/auth-controller";
import { provisioning, type Prospect, type ProspectStage } from "../lib/provisioning";

const STAGE_META: Record<ProspectStage, { label: string; badge: string; icon: string }> = {
  candidate: { label: "Candidate", badge: "badge--neutral", icon: "storefront" },
  contacted: { label: "Contacted", badge: "badge--gi", icon: "campaign" },
  interested: { label: "Interested", badge: "badge--gi", icon: "mail" },
  tenant_created: { label: "Tenant created", badge: "badge--ok", icon: "domain" },
  ebsi_onboarding: { label: "EBSI onboarding", badge: "badge--ok", icon: "hub" },
  active: { label: "Active", badge: "badge--ok", icon: "check_circle" },
  declined: { label: "Declined", badge: "badge--bad", icon: "flag" },
};

// The onboarding pipeline — the board of prospects that Producers hands off to
// via "Start onboarding", tracked from candidate → contacted → interested →
// tenant provisioned → EBSI onboarding → active. Producer sourcing + enrichment
// live in Producers (the registry); this page is purely the conversion funnel.
@customElement("provisioning-page")
export class ProvisioningPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .lead {
        color: var(--md-sys-color-primary);
        cursor: pointer;
        text-decoration: none;
      }
      .stage-actions {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
        justify-content: flex-end;
      }
      .pipeline-counts {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }
    `,
  ];

  @state() private loading = true;
  @state() private error = "";
  @state() private ebsiConfigured = false;
  @state() private prospects: Prospect[] = [];
  @state() private busyId = "";

  private auth = new AuthController(this);

  // Operator-only affordances are hidden from other users (the server enforces
  // it too — no fake buttons).
  private get canManage(): boolean {
    return this.auth.hasRole("platform_operator");
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      const [caps, prospects] = await Promise.all([
        provisioning.capabilities(),
        provisioning.prospects(),
      ]);
      this.ebsiConfigured = caps.ebsiConfigured;
      this.prospects = prospects;
    } catch (err) {
      this.error = errorMessage(err, "Failed to load the pipeline.");
    } finally {
      this.loading = false;
    }
  }

  private async advance(p: Prospect, stage: ProspectStage): Promise<void> {
    this.busyId = p.id;
    try {
      this.replaceProspect(await provisioning.advanceStage(p.id, stage));
    } catch (err) {
      toast(errorMessage(err, "Could not update stage."), true);
    } finally {
      this.busyId = "";
    }
  }

  private async provisionTenant(p: Prospect): Promise<void> {
    this.busyId = p.id;
    try {
      const updated = await provisioning.provisionTenant(p.id);
      this.replaceProspect(updated);
      toast(`Tenant provisioned — invite ${updated.contactEmail || "the contact"} from Team`);
    } catch (err) {
      toast(errorMessage(err, "Could not provision tenant."), true);
    } finally {
      this.busyId = "";
    }
  }

  private async onboardEbsi(p: Prospect): Promise<void> {
    this.busyId = p.id;
    try {
      const updated = await provisioning.startEbsiOnboarding(p.id);
      this.replaceProspect(updated);
      toast("EBSI onboarding started");
      void navigate(`/provisioning/ebsi/${updated.id}`);
    } catch (err) {
      toast(errorMessage(err, "Could not start EBSI onboarding."), true);
    } finally {
      this.busyId = "";
    }
  }

  private replaceProspect(updated: Prospect): void {
    this.prospects = this.prospects.map((x) => (x.id === updated.id ? updated : x));
  }

  private toProducers = (e: Event): void => {
    e.preventDefault();
    void navigate("/companies");
  };

  render() {
    return html`
      <app-shell active="/provisioning">
        <div class="page">
          <div class="page-header">
            <div>
              <h1>Onboarding pipeline</h1>
              <p>
                Producers you've started onboarding (from
                <a class="lead" href="/companies" @click=${this.toProducers}>Producers</a>),
                tracked from first contact through tenant provisioning and EBSI onboarding.
              </p>
            </div>
          </div>

          ${this.loading
            ? html`<div class="center-loading">
                <md-circular-progress indeterminate></md-circular-progress>
              </div>`
            : this.error
              ? html`<p class="error-text">${this.error}</p>`
              : this.renderPipeline()}
        </div>
      </app-shell>
    `;
  }

  private renderPipeline() {
    const counts = new Map<ProspectStage, number>();
    for (const p of this.prospects) counts.set(p.stage, (counts.get(p.stage) ?? 0) + 1);
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>Pipeline <small>· ${this.prospects.length} prospects</small></h2>
          <div class="pipeline-counts">
            ${(Object.keys(STAGE_META) as ProspectStage[])
              .filter((s) => counts.get(s))
              .map(
                (s) => html`<span class="badge ${STAGE_META[s].badge}"
                  >${icon(STAGE_META[s].icon)} ${counts.get(s)} ${STAGE_META[s].label}</span
                >`,
              )}
          </div>
        </div>
        <div class="table-scroll"><table>
          <thead>
            <tr>
              <th>Prospect</th>
              <th>GI good</th>
              <th>Stage</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            ${this.prospects.length === 0
              ? html`<tr>
                  <td colspan="4" class="empty">
                    No prospects yet. Open a producer in
                    <a class="lead" href="/companies" @click=${this.toProducers}>Producers</a>
                    and choose “Start onboarding”.
                  </td>
                </tr>`
              : this.prospects.map((p) => this.renderProspectRow(p))}
          </tbody>
        </table></div>
      </div>
    `;
  }

  private renderProspectRow(p: Prospect) {
    const meta = STAGE_META[p.stage];
    const busy = this.busyId === p.id;
    return html`
      <tr>
        <td>
          <b>${p.legalName}</b>
          <small>${p.siret ? `SIRET ${p.siret}` : p.registeredAddress || "—"}</small>
        </td>
        <td>${p.giProductName}</td>
        <td>
          <span class="badge ${meta.badge}">${icon(meta.icon)} ${meta.label}</span>
          ${p.ebsiRequestedAt && p.stage !== "ebsi_onboarding" && p.stage !== "active"
            ? html`<span
                class="badge badge--warn"
                title=${`Requested ${new Date(p.ebsiRequestedAt).toLocaleString()} by ${p.ebsiRequestedBy || "an org admin"}`}
                >${icon("hourglass_empty")} EBSI requested</span
              >`
            : null}
        </td>
        <td>
          <div class="stage-actions" ?hidden=${!this.canManage}>
            ${p.stage === "candidate"
              ? html`<md-text-button ?disabled=${busy} @click=${() => void this.advance(p, "contacted")}>
                  <span slot="icon">${icon("campaign")}</span>Contact
                </md-text-button>`
              : null}
            ${p.stage === "contacted"
              ? html`<md-text-button ?disabled=${busy} @click=${() => void this.advance(p, "interested")}>
                  <span slot="icon">${icon("mail")}</span>Interested
                </md-text-button>`
              : null}
            ${(p.stage === "interested" || p.stage === "contacted") && !p.tenantOrgId
              ? html`<md-filled-button ?disabled=${busy} @click=${() => void this.provisionTenant(p)}>
                  <span slot="icon">${icon("domain")}</span>Create tenant
                </md-filled-button>`
              : null}
            ${p.tenantOrgId && p.stage !== "ebsi_onboarding" && p.stage !== "active"
              ? html`<md-filled-button
                  ?disabled=${busy || !this.ebsiConfigured}
                  title=${this.ebsiConfigured ? "" : "Set EBSI_PLATFORM_URL to enable"}
                  @click=${() => void this.onboardEbsi(p)}
                >
                  <span slot="icon">${icon("hub")}</span>Onboard to EBSI
                </md-filled-button>`
              : null}
            ${p.stage === "ebsi_onboarding"
              ? html`<md-text-button @click=${() => void navigate(`/provisioning/ebsi/${p.id}`)}>
                  <span slot="icon">${icon("open_in_new")}</span>EBSI console
                </md-text-button>`
              : null}
            ${p.stage !== "declined" && p.stage !== "active" && !p.tenantOrgId
              ? html`<md-text-button ?disabled=${busy} @click=${() => void this.advance(p, "declined")}>
                  Decline
                </md-text-button>`
              : null}
          </div>
        </td>
      </tr>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "provisioning-page": ProvisioningPage;
  }
}
